import { Component } from '@angular/core';

@Component({
  selector: 'app-inspection',
  templateUrl: './inspection.component.html',
  styleUrls: ['./inspection.component.scss']
})
export class InspectionComponent {
  selectedSystemIndex = 0;
  selectedSystemFilter = 'All';
  isFieldJournalSystemModalOpen = false;

  systems = [
    {
      title: 'Exterior Inspection',
      area: 'Envelope',
      group: 'Exterior',
      fieldSignal: 'Water entry, movement, and exterior envelope blueprint markers.',
      decisionLens: 'Which visible exterior concerns should shape repair follow-up?',
      reportNote: 'Photos and plain-language notes help connect exterior conditions to practical next steps.',
      clientQuestion: 'How is the home protected from water and movement?',
      homeownerCue: 'Foundation edges, wall transitions, windows, doors, vents, and exterior penetrations.',
      closingCue: 'Exterior conditions often shape repair priorities, water-management questions, and maintenance planning around the property.',
      prepItems: ['Drainage or grading concerns', 'Recent exterior repairs', 'Seller disclosure notes'],
      mapX: 21,
      mapY: 48,
      imagePosition: 'center bottom',
      image: 'assets/images/inspections-exterior.jpg',
      summary: 'Visible exterior systems are reviewed for conditions that can affect the home envelope and foundation.',
      items: ['Foundation', 'Siding, Brick, Stone, Stucco', 'Windows', 'Doors', 'Sashes and lintels', 'Vents', 'Exterior penetrations', 'All other visible exterior components']
    },
    {
      title: 'Roof Inspection',
      area: 'Water shedding',
      group: 'Exterior',
      fieldSignal: 'Drainage paths, coverings, roof penetrations, and weathering markers.',
      decisionLens: 'Where could weather, drainage, or roof age affect the transaction?',
      reportNote: 'Roof observations are organized so clients can discuss visible concerns with the right trade.',
      clientQuestion: 'Where could weather, drainage, or age affect the roof?',
      homeownerCue: 'Shingles, flashings, vents, gutters, drip edges, skylights, and downspouts.',
      closingCue: 'Roof observations help clients understand weather exposure, useful trade follow-up, and possible repair conversations.',
      prepItems: ['Known leaks or staining', 'Roof age if available', 'Insurance or seller roof notes'],
      mapX: 50,
      mapY: 17,
      imagePosition: 'center top',
      image: 'assets/images/inspections-roof.png',
      summary: 'Roof materials and related visible components are checked for defects, drainage concerns, and wear.',
      items: ['Shingles', 'Flashings', 'Chimneys', 'Vents', 'Trim and gutters', 'Drip edges', 'Skylights', 'Downspouts', 'All other visible roof related items']
    },
    {
      title: 'Attic Inspection',
      area: 'Structure and airflow',
      group: 'Structure',
      fieldSignal: 'Ventilation, insulation, framing members, and moisture indicators.',
      decisionLens: 'What does the hidden structure reveal about maintenance or performance?',
      reportNote: 'Attic notes help clients understand conditions that are easy to miss during a showing.',
      clientQuestion: 'What story does the attic tell about the home?',
      homeownerCue: 'Insulation, ventilation, rafters, joists, collar ties, purlins, and gusset plates.',
      closingCue: 'Attic conditions can connect structure, ventilation, insulation, and moisture clues that buyers do not see during showings.',
      prepItems: ['Prior moisture concerns', 'Energy or comfort issues', 'Known roof or ventilation work'],
      mapX: 50,
      mapY: 29,
      imagePosition: 'center center',
      image: 'assets/images/inspections-attic.jpeg',
      summary: 'Attic conditions help tell the story of ventilation, insulation, structure, and moisture-related clues.',
      items: ['Insulation and baffles', 'Ventilation', 'Rafters', 'Joists and collar ties', 'Purlins', 'Gusset plates', 'All other visible attic related items']
    },
    {
      title: 'HVAC System Inspection',
      area: 'Comfort systems',
      group: 'Mechanical',
      fieldSignal: 'Equipment condition, airflow clues, filters, ductwork, and temperature split readings.',
      decisionLens: 'What visible HVAC clues should be understood before closing or planning repairs?',
      reportNote: 'Mechanical notes translate visible system conditions into a clearer comfort-system discussion.',
      clientQuestion: 'What visible clues affect heating, cooling, and airflow?',
      homeownerCue: 'A/C condenser, furnace, filters, ductwork, and system differential readings.',
      closingCue: 'HVAC notes help clients understand visible comfort-system condition before repair planning or seasonal use.',
      prepItems: ['System age if known', 'Service records', 'Comfort or airflow concerns'],
      mapX: 88,
      mapY: 72,
      imagePosition: 'center center',
      image: 'assets/images/inspections-hvac.jpeg',
      summary: 'Heating and cooling equipment is reviewed so clients understand visible system condition and performance clues.',
      items: ['A/C condenser', 'Furnace', 'Filters', 'Ductwork', 'Differential readings of HVAC systems', 'All other visible HVAC related components']
    },
    {
      title: 'Interior Inspections',
      area: 'Living spaces',
      group: 'Interior',
      fieldSignal: 'Everyday-use clues across rooms, stairs, finishes, fixtures, and appliances.',
      decisionLens: 'Which interior items are safety, usability, or maintenance priorities?',
      reportNote: 'Interior observations keep the report grounded in how the home will actually be used.',
      clientQuestion: 'What visible interior conditions affect safety and everyday use?',
      homeownerCue: 'Floors, ceilings, walls, stairs, fireplaces, sinks, and major appliances.',
      closingCue: 'Interior findings help separate everyday usability, safety items, and maintenance concerns from larger system issues.',
      prepItems: ['Known appliance concerns', 'Visible staining or cracks', 'Items noticed during walkthrough'],
      mapX: 53,
      mapY: 62,
      imagePosition: 'center center',
      image: 'assets/images/inspections-interior.jpeg',
      summary: 'Interior rooms are reviewed for visible conditions affecting usability, safety, and maintenance planning.',
      items: ['Floors and Ceilings', 'Interior Walls', 'Stairs and banisters', 'Fireplaces', 'Sinks', 'Major Appliances', 'Any other visible interior components']
    },
    {
      title: 'Electrical Inspections',
      area: 'Power and safety',
      group: 'Electrical',
      fieldSignal: 'Panels, clearances, branch circuits, fixtures, grounding, bonding, and visible wiring.',
      decisionLens: 'Which visible electrical conditions need qualified follow-up?',
      reportNote: 'Electrical notes are structured carefully so clients can distinguish visible observations from trade repair work.',
      clientQuestion: 'What should be understood about visible electrical components?',
      homeownerCue: 'Service entries, panels, branch circuits, switches, receptacles, fixtures, wiring, grounding, and bonding.',
      closingCue: 'Electrical observations help clients identify visible safety concerns and questions for qualified electrical follow-up.',
      prepItems: ['Known outlet or breaker issues', 'Recent electrical work', 'Panel access details'],
      mapX: 27,
      mapY: 61,
      imagePosition: 'center center',
      image: 'assets/images/inspections-electrical.jpeg',
      summary: 'Visible electrical components are reviewed with attention to panels, circuits, fixtures, grounding, and bonding.',
      items: ['Service entries and clearances', 'Main service panels and sub panels', 'Branch circuits', 'Light switches and receptacles', 'Light Fixtures', 'Visible wiring and junction boxes', 'Grounding and bonding systems', 'All other visible electrical components']
    },
    {
      title: 'Plumbing Inspection',
      area: 'Water and fixtures',
      group: 'Plumbing',
      fieldSignal: 'Supply entry points, pressure clues, fixtures, traps, drains, and visible water concerns.',
      decisionLens: 'What visible plumbing conditions affect repair planning or everyday use?',
      reportNote: 'Plumbing notes connect supply, fixture, and drainage observations to useful client questions.',
      clientQuestion: 'How are visible water supply and fixture conditions performing?',
      homeownerCue: 'Water meter, cutoff, PRV valve, pressure, hose bibs, sinks, baths, showers, toilets, traps, and overflows.',
      closingCue: 'Plumbing findings can affect everyday use, repair estimates, and questions around water pressure or visible leaks.',
      prepItems: ['Known leaks or slow drains', 'Water heater details', 'Fixture or pressure concerns'],
      mapX: 59,
      mapY: 66,
      imagePosition: '18% center',
      image: 'assets/images/inspections-plumbing.jpeg',
      summary: 'Visible plumbing components are checked from supply entry points to fixtures, pressure, traps, and drains.',
      items: ['Water meter, water cutoff, and PRV valve', 'Water Pressure', 'Hose bibs', 'Sinks and faucets', 'Baths, showers, and toilets', 'P-traps and overflows', 'All other visible plumbing related items']
    }
  ];

  systemFilters = [
    'All',
    'Exterior',
    'Structure',
    'Mechanical',
    'Interior',
    'Electrical',
    'Plumbing'
  ];

  scopeStats = [
    {
      value: '7',
      label: 'system sheets',
      description: 'Organized into blueprint-style modules.'
    },
    {
      value: '52',
      label: 'checkpoints',
      description: 'Structured for client scanning and report review.'
    },
    {
      value: '1',
      label: 'clear report',
      description: 'Built to support the next property decision.'
    }
  ];

  walkthroughMoments = [
    {
      title: 'Start with the property plan',
      description: 'Roland moves through the home with a practical scope so the inspection stays organized.',
      image: 'assets/images/inspections-exterior.jpg'
    },
    {
      title: 'Capture field evidence and questions',
      description: 'Findings are explained in plain language so buyers, sellers, agents, and owners understand context.',
      image: 'assets/images/inspections-electrical.jpeg'
    },
    {
      title: 'Leave with useful blueprint notes',
      description: 'The report documents visible areas of concern in a format that supports informed decisions.',
      image: 'assets/images/inspections-interior.jpeg'
    }
  ];

  selectSystem(index: number): void {
    this.selectedSystemIndex = index;
  }

  selectSystemByTitle(title: string): void {
    const nextIndex = this.systems.findIndex((system) => system.title === title);
    if (nextIndex >= 0) {
      this.selectedSystemIndex = nextIndex;
    }
  }

  openFieldJournalSystemModal(title: string): void {
    this.selectSystemByTitle(title);
    this.isFieldJournalSystemModalOpen = true;
  }

  closeFieldJournalSystemModal(): void {
    this.isFieldJournalSystemModalOpen = false;
  }

  selectSystemFilter(filter: string): void {
    this.selectedSystemFilter = filter;
    const selectedIsVisible = this.visibleSystems.some((system) => system.title === this.selectedSystem.title);
    if (!selectedIsVisible && this.visibleSystems.length > 0) {
      this.selectSystemByTitle(this.visibleSystems[0].title);
    }
  }

  selectPreviousSystem(): void {
    this.selectedSystemIndex = this.selectedSystemIndex === 0 ? this.systems.length - 1 : this.selectedSystemIndex - 1;
    this.selectedSystemFilter = 'All';
  }

  selectNextSystem(): void {
    this.selectedSystemIndex = this.selectedSystemIndex === this.systems.length - 1 ? 0 : this.selectedSystemIndex + 1;
    this.selectedSystemFilter = 'All';
  }

  get selectedSystem() {
    return this.systems[this.selectedSystemIndex];
  }

  get selectedSystemNumber(): number {
    return this.selectedSystemIndex + 1;
  }

  get selectedSystemProgress(): number {
    return Math.round(((this.selectedSystemIndex + 1) / this.systems.length) * 100);
  }

  get visibleSystems() {
    if (this.selectedSystemFilter === 'All') {
      return this.systems;
    }

    return this.systems.filter((system) => system.group === this.selectedSystemFilter);
  }

  get visibleCheckpointCount(): number {
    return this.visibleSystems.reduce((total, system) => total + system.items.length, 0);
  }

  get previousSystemLabel(): string {
    return this.selectedSystemIndex === 0
      ? this.systems[this.systems.length - 1].title
      : this.systems[this.selectedSystemIndex - 1].title;
  }

  get nextSystemLabel(): string {
    return this.selectedSystemIndex === this.systems.length - 1
      ? this.systems[0].title
      : this.systems[this.selectedSystemIndex + 1].title;
  }

  get totalCheckpoints(): number {
    return this.systems.reduce((total, system) => total + system.items.length, 0);
  }

}
